export type DiffKind = 'added' | 'removed' | 'changed' | 'unchanged';

export interface DiffEntry {
  /** Dotted path (`settings.branding.primaryColor`, `scopes.0`). */
  path: string;
  before: unknown;
  after: unknown;
  kind: DiffKind;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Leaf values by dotted path; empty objects/arrays are leaves themselves. */
export function flattenValue(
  value: unknown,
  prefix = '',
  out = new Map<string, unknown>(),
): Map<string, unknown> {
  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      // An empty root snapshot has no leaves (created/deleted entries); nested empties are leaves.
      if (prefix) out.set(prefix, value);
      return out;
    }
    for (const [key, child] of entries) flattenValue(child, prefix ? `${prefix}.${key}` : key, out);
    return out;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      if (prefix) out.set(prefix, value);
      return out;
    }
    for (const [index, child] of value.entries()) {
      flattenValue(child, prefix ? `${prefix}.${index}` : String(index), out);
    }
    return out;
  }
  out.set(prefix || '(root)', value);
  return out;
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** Leaf-level diff of two audit snapshots, sorted by path. `null` snapshots count as empty. */
export function diffJson(before: unknown, after: unknown): DiffEntry[] {
  const left = flattenValue(before ?? {});
  const right = flattenValue(after ?? {});
  const paths = [...new Set([...left.keys(), ...right.keys()])].sort();
  return paths.map((path) => {
    const inLeft = left.has(path);
    const inRight = right.has(path);
    const b = left.get(path);
    const a = right.get(path);
    const kind: DiffKind = !inLeft
      ? 'added'
      : !inRight
        ? 'removed'
        : same(a, b)
          ? 'unchanged'
          : 'changed';
    return { path, before: b, after: a, kind };
  });
}

export interface DiffSummary {
  added: number;
  removed: number;
  changed: number;
  unchanged: number;
}

export function summarizeDiff(entries: readonly DiffEntry[]): DiffSummary {
  const summary: DiffSummary = { added: 0, removed: 0, changed: 0, unchanged: 0 };
  for (const entry of entries) summary[entry.kind] += 1;
  return summary;
}

/** Compact one-line rendering of a leaf value for the diff table. */
export function formatDiffValue(value: unknown): string {
  if (value === undefined) return '';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}
